// Reusable pop-out opener for Study Chat
// Can be imported by any host app/site.
//
// Usage:
//   openChatPopout({ groupId: 123 });
//   openChatPopout({ baseOrigin: 'https://myapp.com', route: '/study-chat/popout', groupId: 'abc' });
//   openChatPopout({ groupId: 42, subjectId: 216, subjectTitle: 'Embedded & IoT Foundations' });

export type PopoutOptions = {
  /** Absolute origin for cross-site use. Defaults to window.location.origin */
  baseOrigin?: string;
  /** Route path to the popout page in the host app */
  route?: string; // default: '/study-chat/popout'
  /** Query params to forward (groupId recommended) */
  query?: Record<string, string | number | boolean | undefined>;
  /** Convenience params for most cases */
  groupId?: string | number;
  subjectId?: string | number;
  /** Optional friendly name for the subject; forwarded as ?title= */
  subjectTitle?: string;

  /** Window features */
  width?: number;   // default: 520
  height?: number;  // default: 760
  center?: boolean; // default: true
  windowName?: string; // default: 'study-chat-popout'
  /** Extra raw features to append (e.g., 'resizable=yes') */
  features?: string;
};

/** Open the Study Chat in a centered popup window. Falls back to same-tab if blocked. */
export function openChatPopout(opts: PopoutOptions = {}) {
  const {
    baseOrigin = window.location.origin,
    route = '/study-chat/popout',
    groupId,
    subjectId,
    subjectTitle,
    query = {},
    width = 520,
    height = 760,
    center = true,
    windowName = 'study-chat-popout',
    features = 'resizable=yes',
  } = opts;

  // Build query string
  const params = new URLSearchParams();
  if (groupId !== undefined) params.set('groupId', String(groupId));
  if (subjectId !== undefined) params.set('subjectId', String(subjectId));
  if (subjectTitle) params.set('title', subjectTitle);
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, String(v));
  }

  const base = new URL(route, baseOrigin); // works with absolute or relative route
  if ([...params.keys()].length) base.search = params.toString();

  // Center on current screen
  let left: number | undefined;
  let top: number | undefined;
  if (center) {
    const dualLeft = (window.screenLeft ?? window.screenX ?? 0) as number;
    const dualTop  = (window.screenTop  ?? window.screenY ?? 0) as number;
    const ww = (window.innerWidth ?? document.documentElement.clientWidth ?? screen.width) as number;
    const wh = (window.innerHeight ?? document.documentElement.clientHeight ?? screen.height) as number;
    left = Math.max(0, Math.round(dualLeft + (ww - width) / 2));
    top  = Math.max(0, Math.round(dualTop  + (wh - height) / 2));
  }

  const featureStr = [
    `popup=yes`,
    `width=${width}`,
    `height=${height}`,
    left !== undefined ? `left=${left}` : '',
    top  !== undefined ? `top=${top}` : '',
    features,
  ]
    .filter(Boolean)
    .join(',');

  const win = window.open(base.toString(), windowName, featureStr);

  // Fallback (blocked popups)
  if (!win || win.closed) {
    window.location.href = base.toString();
  }

  return win;
}
